import type { FormSchemaResolved } from '#layers/block-engine';
import { get } from 'lodash-es';

export const OIDC_SCOPES = ['openid', 'email', 'profile'] as const;
export const OIDC_CLAIM_MAPPINGS = { sub: 'sub', email: 'email' } as const;

export interface OAuth2PresetDefaults {
	authorizationEndpoint: string;
	tokenEndpoint: string;
	userinfoEndpoint: string;
	scopes: readonly string[];
	claimMappings: { sub: string; email?: string };
	extraAuthorizeParams?: Record<string, string>;
}

interface ApplyOAuth2PresetOptions {
	defaults?: Record<string, unknown>;
	descriptions?: Record<string, string>;
	omitFieldNames?: readonly string[];
	omitSectionLabels?: readonly string[];
	fieldOverrides?: Record<string, Record<string, unknown>>;
}

// @TODO this feels too generic to be here ~ Rijk 6/19/26
interface FormGroup {
	type: 'group';
	label?: unknown;
	defaultOpen?: boolean;
	collapsible?: boolean;
	fields: FormSchemaResolved<any>;
}

export function oauth2PresetDefaults(defaults: OAuth2PresetDefaults): Record<string, unknown> {
	const mappedDefaults: Record<string, unknown> = {
		'authorizationEndpoint': defaults.authorizationEndpoint,
		'tokenEndpoint': defaults.tokenEndpoint,
		'userinfoEndpoint': defaults.userinfoEndpoint,
		'scopes': defaults.scopes,
		'claimMappings.sub': defaults.claimMappings.sub,
		'extraAuthorizeParams': Object.entries(defaults.extraAuthorizeParams ?? {})
			.map(([key, value]) => ({ key, value })),
	};

	if (defaults.claimMappings.email) mappedDefaults['claimMappings.email'] = defaults.claimMappings.email;

	return mappedDefaults;
}

export function applyOAuth2PresetOptions(
	items: FormSchemaResolved<any>,
	options: ApplyOAuth2PresetOptions,
): FormSchemaResolved<any> {
	const omitFieldNames = new Set(options.omitFieldNames);
	const omitSectionLabels = new Set(options.omitSectionLabels);
	const resolved: FormSchemaResolved<any> = [];

	for (const item of items) {
		const itemHasName = 'name' in item && !!item.name;

		if (itemHasName && omitFieldNames.has(item.name)) continue;

		const itemType: string | null = get(item, 'type', null);
		const itemLabel: string | null = get(item, 'label', null);

		if (
			itemType === 'separator'
			&& itemLabel
			&& omitSectionLabels.has(itemLabel)
		) {
			continue;
		}

		if (itemType === 'group') {
			const group = item as FormGroup;

			resolved.push({
				...group,
				defaultOpen: false,
				fields: applyOAuth2PresetOptions(group.fields, options),
			} as typeof item);

			continue;
		}

		let field = item;

		if (!itemHasName) {
			resolved.push(field);
			continue;
		}

		if (options.defaults && field.name in options.defaults) {
			field.default = () => options.defaults![field.name!];
		}

		if (options.descriptions && field.name in options.descriptions) {
			field.description = options.descriptions[field.name]!;
		}

		if (options.fieldOverrides && field.name in options.fieldOverrides) {
			field = { ...field, ...options.fieldOverrides[field.name] };
		}

		resolved.push(field);
	}

	return resolved;
}

export function promoteOAuth2PresetFields(
	items: FormSchemaResolved<any>,
	fieldNames: readonly string[],
): FormSchemaResolved<any> {
	const names = new Set(fieldNames);
	const promoted = new Map<string, FormSchemaResolved<any>[number]>();

	const remaining = removePromotedFields(items, names, promoted);
	const primaryFields = fieldNames.flatMap((name) => promoted.get(name) ?? []);
	const firstGroupIndex = remaining.findIndex((item) => item && 'type' in item && item.type === 'group');

	if (firstGroupIndex === -1) return [...remaining, ...primaryFields];

	return [
		...remaining.slice(0, firstGroupIndex),
		...primaryFields,
		...remaining.slice(firstGroupIndex),
	];
}

export function promoteOAuth2PresetFieldsBeforeCredentials(
	items: FormSchemaResolved<any>,
	fieldNames: readonly string[],
): FormSchemaResolved<any> {
	const names = new Set(fieldNames);
	const promoted = new Map<string, FormSchemaResolved<any>[number]>();
	const remaining = removePromotedFields(items, names, promoted);
	const primaryFields = fieldNames.flatMap((name) => promoted.get(name) ?? []);

	return [
		...primaryFields,
		...remaining,
	];
}

function removePromotedFields(
	items: FormSchemaResolved<any>,
	names: Set<string>,
	promoted: Map<string, FormSchemaResolved<any>[number]>,
): FormSchemaResolved<any> {
	const remaining: FormSchemaResolved<any> = [];

	for (const item of items) {
		if ('name' in item && item.name && names.has(item.name)) {
			promoted.set(item.name, item);
			continue;
		}

		if (item && 'type' in item && item.type === 'group') {
			const group = item as FormGroup;

			remaining.push({
				...group,
				fields: removePromotedFields(group.fields, names, promoted),
			} as typeof item);

			continue;
		}

		remaining.push(item);
	}

	return remaining;
}

export function normalizeDomain(value: string, suffix?: string): string {
	const domain = value
		.trim()
		.replace(/^https?:\/\//, '')
		.replace(/\/.*$/, '');

	if (!suffix || domain.endsWith(suffix) || domain.includes('.')) return domain;

	return `${domain}${suffix}`;
}
